/** A usage meter (design v2 § Meter): ink for used, a hatch for what in-flight work holds, a
 * warn hatch past the allowance. Usage isn't progress, so no accent. */
import type { Meter as MeterData } from "../api/api";

const n = (x: number) => x.toLocaleString("en-GB");

function pct(x: number, of: number): string {
  return `${Math.max(0, Math.min(100, (x / of) * 100))}%`;
}

export function Meter({
  label,
  meter,
  large = false,
  heldWords = "held",
}: {
  label: string;
  meter: Pick<MeterData, "used" | "held" | "limit">;
  large?: boolean;
  /** "held", or on the account page "held for work in progress". */
  heldWords?: string;
}) {
  const { used, held, limit } = meter;
  const cap = Math.max(limit, 1);
  const over = Math.max(used + held - limit, 0);
  const whole = Math.max(limit, used + held, 1);
  return (
    <div className={`meter${large ? " meter--large" : ""}`}>
      <div className="meter__label">
        <span>{label}</span>
        <span className="meter__figure">
          <strong>{n(used)}</strong> of {n(limit)}
          {held > 0 && ` · ${n(held)} ${heldWords}`}
        </span>
      </div>
      <div
        className="meter__track"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={used}
        aria-valuetext={`${n(used)} of ${n(limit)}${held > 0 ? `, ${n(held)} held` : ""}`}
      >
        <span className="meter__used" style={{ width: pct(Math.min(used, cap), whole) }} />
        <span
          className="meter__held"
          style={{ width: pct(Math.max(Math.min(used + held, cap) - used, 0), whole) }}
        />
        {over > 0 && <span className="meter__over" style={{ width: pct(over, whole) }} />}
      </div>
    </div>
  );
}

/** The header's two stacked 28 × 3 bars; a bar past 90% turns warn. */
export function MiniMeters({ meters }: { meters: Pick<MeterData, "used" | "limit">[] }) {
  return (
    <span className="mini-meters" aria-hidden="true">
      {meters.map((m, i) => {
        const f = m.limit > 0 ? m.used / m.limit : 0;
        return (
          <span key={i} className={`mini-meters__bar${f > 0.9 ? " mini-meters__bar--warn" : ""}`}>
            <span style={{ width: pct(m.used, Math.max(m.limit, 1)) }} />
          </span>
        );
      })}
    </span>
  );
}

/** A count too small for a bar (subjects): one cell per place, filled as they are used. */
export function CellMeter({
  label,
  meter,
}: {
  label: string;
  meter: Pick<MeterData, "used" | "limit">;
}) {
  const { used, limit } = meter;
  return (
    <div className="meter">
      <div className="meter__label">
        <span>{label}</span>
        <span className="meter__figure">
          <strong>{n(used)}</strong> of {n(limit)}
        </span>
      </div>
      <div
        className="meter__cells"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={used}
        aria-valuetext={`${n(used)} of ${n(limit)}`}
      >
        {Array.from({ length: limit }, (_, i) => (
          <span key={i} className={i < used ? "meter__cell meter__cell--used" : "meter__cell"} />
        ))}
      </div>
    </div>
  );
}
